/**
 * PWA Install Prompt — Progressive Web App install encouragement banner.
 * Extracted from components/notifications/pwa-prompt.blade.php to reduce Blade tokenization overhead.
 *
 * Pure vanilla JS — no Blade interpolation dependencies.
 */
export default function initPwaPrompt() {
  'use strict';

  var STORAGE_KEY = 'pwa_install_prompt_status';
  var SHOW_DELAY = 150000; // 2.5 minutes
  var COOLDOWN_DAYS = 7;

  var banner = document.getElementById('pwa-install-prompt');
  var installBtn = document.getElementById('pwa-install-accept');
  var dismissBtn = document.getElementById('pwa-install-dismiss');

  var showTimeout = null;
  var cookieConsentObserver = null;

  function getAppStore() {
    return new Promise(function (resolve) {
      var check = function () {
        var store =
          window.Alpine && window.Alpine.store && window.Alpine.store('app');
        if (store) {
          resolve(store);
        } else {
          setTimeout(check, 100);
        }
      };
      check();
    });
  }

  function isStandalone() {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true
    );
  }

  function getStatus() {
    try {
      var stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  }

  function saveStatus(dismissed, installed) {
    dismissed = dismissed || false;
    installed = installed || false;
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          dismissed: dismissed,
          installed: installed,
          timestamp: new Date().toISOString(),
        })
      );
    } catch (e) {
      console.warn('[PWA Install] Could not save status');
    }
  }

  function daysSince(isoTimestamp) {
    var then = new Date(isoTimestamp);
    var now = new Date();
    return (now - then) / (1000 * 60 * 60 * 24);
  }

  function shouldShow(status) {
    if (status && status.installed) {
      return false;
    }

    if (status && status.dismissed && status.timestamp) {
      var days = daysSince(status.timestamp);
      if (days < COOLDOWN_DAYS) {
        return false;
      }
    }

    return true;
  }

  function hideBanner() {
    if (!banner) return;
    banner.setAttribute('data-hidden', '');
    setTimeout(function () {
      banner.setAttribute('aria-hidden', 'true');
      banner.querySelectorAll('button').forEach(function (btn) {
        btn.setAttribute('tabindex', '-1');
      });
    }, 300);
  }

  function showBanner() {
    if (!banner) return;
    banner.setAttribute('aria-hidden', 'false');
    banner.querySelectorAll('button').forEach(function (btn) {
      btn.removeAttribute('tabindex');
    });
    banner.offsetHeight;
    banner.removeAttribute('data-hidden');
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'pwa_install_prompt_shown');
    }
  }

  function isCookieConsentVisible() {
    var cookieConsent = document.getElementById('cookie-consent-banner');
    if (!cookieConsent) return false;
    return !cookieConsent.hasAttribute('data-hidden');
  }

  function waitForCookieConsent() {
    return new Promise(function (resolve) {
      var cookieConsent = document.getElementById('cookie-consent-banner');

      if (!cookieConsent) {
        resolve();
        return;
      }

      if (!isCookieConsentVisible()) {
        resolve();
        return;
      }

      cookieConsentObserver = new MutationObserver(function () {
        if (!isCookieConsentVisible()) {
          cookieConsentObserver.disconnect();
          cookieConsentObserver = null;
          resolve();
        }
      });

      cookieConsentObserver.observe(cookieConsent, {
        attributes: true,
        attributeFilter: ['data-hidden'],
      });
    });
  }

  async function handleInstall() {
    try {
      var store = await getAppStore();
      var success = await store.promptInstall();

      if (success) {
        saveStatus(false, true);
        hideBanner();
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'pwa_install_accepted');
        }
        store.showToast('App installed successfully!', 'success');
      } else {
        saveStatus(true, false);
        hideBanner();
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'pwa_install_dismissed');
        }
      }
    } catch (error) {
      console.error('[PWA Install] Install failed:', error);
      var store = await getAppStore();
      store.showToast('Could not install app. Try again later.', 'error');
    }
  }

  function handleDismiss() {
    saveStatus(true, false);
    hideBanner();
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'pwa_install_dismissed');
    }
  }

  async function init() {
    if (!banner) return;

    if (isStandalone()) {
      return;
    }

    var status = getStatus();
    if (!shouldShow(status)) {
      return;
    }

    var store = await getAppStore();

    if (!store.canInstall) {
      var checkInstallable = function () {
        if (store.canInstall && !store.isStandalone) {
          schedulePrompt();
        }
      };
      var checkInterval = setInterval(function () {
        checkInstallable();
        if (store.canInstall) {
          clearInterval(checkInterval);
        }
      }, 1000);
      setTimeout(function () {
        clearInterval(checkInterval);
      }, 30000);
      return;
    }

    schedulePrompt();
  }

  async function schedulePrompt() {
    showTimeout = setTimeout(async function () {
      await waitForCookieConsent();

      var store = await getAppStore();
      if (store.canInstall && !store.isStandalone) {
        showBanner();
      }
    }, SHOW_DELAY);

    if (installBtn) installBtn.addEventListener('click', handleInstall);
    if (dismissBtn) dismissBtn.addEventListener('click', handleDismiss);

    window.addEventListener('beforeunload', function () {
      if (showTimeout) {
        clearTimeout(showTimeout);
      }
      if (cookieConsentObserver) {
        cookieConsentObserver.disconnect();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}
